import React, { useState, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Button } from "../../atoms/Buttons/Base";
import { Fa } from "../../atoms/Icons/Fa";
import { Text } from "../../atoms/Text/Text";
import { Game } from "../../models/Game";
import { ISeachResult, SearchService } from "../../services/Search";
import classNames from "classnames";
import styles from "./Search.css";
import { Box } from "../../atoms/Layout/Box/Box";
import { Analytics } from "../../services/Analytics/Analytics";
import { isMobileDevice } from "../../../utils";
import ThemeSettings from "../../../theme";

type SearchProps = {
  games: Game[];
  className?: string;
  placeholder?: string;
};

const DEFAULT_PLACEHOLDER = "Search Games here";

const Search: React.FC<SearchProps> = ({ games, className, placeholder }) => {
  const [results, setResults] = useState<ISeachResult[]>([]);
  const [value, setValue] = useState("");
  const [showInput, setShowInput] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showInput]);

  const toggleInput = () => {
    setShowInput(prev => !prev);
    if (!showInput) {
      clearSearch();
    }
  };

  const onDocumentClick = () => {
    setResults([]);
  };

  const clearSearch = () => {
    setResults([]);
    setValue("");
  };

  const onFocus = () => {
    Analytics.trackEvent(Analytics.general.searchBoxStart());
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace("%", "");
    const filteredResults = SearchService.quickSearch(
      games.filter((g) => !g.seo_IsHidden),
      val
    );
    setResults(filteredResults);
    setValue(val);
    Analytics.trackEvent(Analytics.general.searchBoxQuery(val));
  };

  const trackClick = (game: Game) => {
    Analytics.trackEvent(Analytics.general.searchBoxSelect(value, game));
  };

  useEffect(() => {
    window.addEventListener("click", onDocumentClick);
    return () => {
      window.removeEventListener("click", onDocumentClick);
    };
  }, []);

  const list = results.length ? (
    <SearchResultsList
      direction="column"
      elevated={true}
      row="grid"
      id="searchGrid"
    >
      {results.map((item, ind) => {
        let link = "";
        let isSpain = null;
        let isFakeGame = null;
        if (item.game) {
          let currentGame = new Game(item.game);
          isSpain = currentGame.isSpain();
          isFakeGame = currentGame.seo_IsFakeGame;
          link = isFakeGame
            ? currentGame.seo_FinalURL
            : Game.getURLAlias(currentGame);
        }
        return (
          <SearchResultAccessibility
            row="row"
            {...(isSpain ? { lang: "es" } : null)}
            key={item.title}
          >
            <SearchLink
              data-element-description="search result"
              to={link}
              href={link}
              onClick={() => trackClick(item.game)}
              key={`${ind}_${item.title}`}
              isFakeGame={isFakeGame}
            >
              <SearchResultsListItem key={`${item.title}`}>
                <Text size="md" color="main" bold={true} key={`${item.title}`}>
                  {item.title}
                </Text>
              </SearchResultsListItem>
            </SearchLink>
          </SearchResultAccessibility>
        );
      })}
    </SearchResultsList>
  ) : null;

  const showSmallButton = isMobileDevice() || window.innerWidth < ThemeSettings.breakPoints.desktop;

  return !showSmallButton ? (
    <SearchContainer className={className}>
      <SearchAccessibility
        role="combobox"
        aria-expanded="false"
        aria-owns="searchGrid"
        aria-haspopup="grid"
      >
        <input
          ref={inputRef}
          className={styles.input}
          aria-label="Search Input"
          data-element-description="search input"
          type="text"
          placeholder={placeholder || DEFAULT_PLACEHOLDER}
          value={value}
          onChange={onSearchChange}
          onFocus={onFocus}
          aria-autocomplete="list"
          aria-controls={list && "searchGrid"}
        />
        {value ? (
          <ClearButton onClick={clearSearch}>
            <Fa icon="times" />
          </ClearButton>
        ) : (
          <SearchIcon onClick={toggleInput}>
            <Fa icon="search" />
          </SearchIcon>
        )}
      </SearchAccessibility>
      {list}
    </SearchContainer>
  ) : (
    <>
      {!showInput ? (
        <SearchIcon onClick={toggleInput}>
          <Fa icon="search" />
        </SearchIcon>
      ) : (
        <SearchContainer className={className}>
        <SearchAccessibility
          role="combobox"
          aria-expanded="false"
          aria-owns="searchGrid"
          aria-haspopup="grid"
        >
          <input
            ref={inputRef}
            className={styles.input}
            aria-label="Search Input"
            data-element-description="search input"
            type="text"
            placeholder={placeholder || DEFAULT_PLACEHOLDER}
            value={value}
            onChange={onSearchChange}
            onFocus={onFocus}
            aria-autocomplete="list"
            aria-controls={list && "searchGrid"}
          />
            <ClearButton onClick={toggleInput}>
              <Fa icon="times" />
            </ClearButton>
          
        </SearchAccessibility>
        {list}
      </SearchContainer>
      )}
    </>
  );
};

const SearchContainer = ({ className, ...props }: any) => (
  <div className={classNames(styles.searchContainer, className)} {...props} />
);
const SearchResultsList = (props: any) => (
  <Box className={styles.searchResultsList} {...props} />
);
const SearchResultsListItem = (props: any) => (
  <div className={styles.searchResultsListItem} {...props} />
);
const SearchIcon = (props: any) => (
  <div className={styles.searchButton} {...props} />
);
const ClearButton = (props: any) => (
  <Button className={styles.searchButton} {...props} />
);

const SearchLink = ({ isFakeGame, ...props }: any) => {
  return isFakeGame ? (
    <a
      className={styles.searchLink}
      {...props}
      target="_blank"
      rel="noopener"
    />
  ) : (
    <RouterLink className={styles.searchLink} {...props} />
  );
};

const SearchAccessibility = (props: any) => (
  <div className={styles.searchAccessibility} {...props} />
);
const SearchResultAccessibility = (props: any) => (
  <div className={styles.searchResultAccessibility} {...props} />
);

export default Search;
